import { useSyncExternalStore } from 'react';

import { useRequestInfo } from '../request-info';
import { type Theme } from '../types';

type ThemeMode = Theme | 'system';

const themeStorageKey = 'veraclins_theme';
const themeChangeEvent = 'veraclins-theme-change';

function isThemeMode(value: unknown): value is ThemeMode {
  return value === 'light' || value === 'dark' || value === 'system';
}

function readClientThemeMode(): ThemeMode | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }

  try {
    const storedTheme = window.localStorage.getItem(themeStorageKey);
    return isThemeMode(storedTheme) ? storedTheme : undefined;
  } catch {
    return undefined;
  }
}

function subscribeToClientThemeMode(onChange: () => void) {
  if (typeof window === 'undefined') {
    return () => undefined;
  }

  const handleStorage = (event: StorageEvent) => {
    if (event.key === themeStorageKey) {
      onChange();
    }
  };

  window.addEventListener('storage', handleStorage);
  window.addEventListener(themeChangeEvent, onChange);

  return () => {
    window.removeEventListener('storage', handleStorage);
    window.removeEventListener(themeChangeEvent, onChange);
  };
}

function useClientThemeMode() {
  return useSyncExternalStore(
    subscribeToClientThemeMode,
    readClientThemeMode,
    () => undefined,
  );
}

export function setClientThemeMode(theme: ThemeMode) {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(themeStorageKey, theme);
    window.dispatchEvent(new Event(themeChangeEvent));
  } catch {
    // Ignore storage failures and fall back to the current request/client hint theme.
  }
}

/**
 * @returns the user's theme preference, or the client hint theme if the user
 * has not set a preference.
 */
export function useTheme(): Theme {
  const { hints, userPrefs } = useRequestInfo();
  const clientMode = useClientThemeMode();

  if (clientMode) {
    return clientMode === 'system' ? hints.theme : clientMode;
  }

  return userPrefs.theme ?? hints.theme;
}

/**
 * @returns the current theme mode (including 'system') for display purposes
 */
export function useThemeMode(): ThemeMode {
  const { userPrefs } = useRequestInfo();
  const clientMode = useClientThemeMode();

  if (clientMode) {
    return clientMode;
  }

  // If no cookie is set, return 'system' to indicate using system preference
  return userPrefs.theme ?? 'system';
}
